import React, { useMemo, useCallback } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Select,
  MenuItem,
  TextField,
  Typography,
  Chip,
  ListSubheader,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import JsonPreview from "./JsonPreview";
import type { SyncSelectedQuestion, SyncMappingRow, SyncSource } from "../types/mapping";
import {
  EVENT_ATTRIBUTES,
  FARM_PROPERTIES,
  MappingFormat,
  MappingEncode,
  templateToJson,
  createEmptyRow,
  sourceLabel,
  suggestTargetField,
} from "../types/mapping";

interface FieldMappingBuilderProps {
  rows: SyncMappingRow[];
  onRowsChange: (rows: SyncMappingRow[]) => void;
  currentQuestion: SyncSelectedQuestion;
}

const sourceKey = (s: SyncSource): string => {
  switch (s.kind) {
    case "entityId":
      return "entityId";
    case "constant":
      return "__new_constant";
    case "eventAttribute":
      return `eventAttribute:${s.attributeName}`;
    case "resolvedValue":
      return `resolvedValue:${s.valueKey}`;
    case "question":
      return `answerValue`;
  }
};

const FieldMappingBuilder: React.FC<FieldMappingBuilderProps> = ({
  rows,
  onRowsChange,
  currentQuestion,
}) => {
  const updateRow = useCallback(
    (id: string, updates: Partial<SyncMappingRow>) => {
      onRowsChange(rows.map((r) => (r.id === id ? { ...r, ...updates } : r)));
    },
    [rows, onRowsChange]
  );

  const removeRow = useCallback(
    (id: string) => {
      onRowsChange(rows.filter((r) => r.id !== id));
    },
    [rows, onRowsChange]
  );

  const handleAddRow = useCallback(() => {
    onRowsChange([...rows, createEmptyRow()]);
  }, [rows, onRowsChange]);

  const handleSourceChange = useCallback(
    (rowId: string, newSource: SyncSource) => {
      const label = sourceLabel(newSource, currentQuestion);
      const suggested = suggestTargetField(newSource, currentQuestion);
      onRowsChange(
        rows.map((r) =>
          r.id === rowId
            ? {
                ...r,
                source: newSource,
                displayLabel: label,
                targetField: r.targetField || suggested,
                encode: newSource.kind === "question" && currentQuestion.isFile ? "base64" : "none",
                format:
                  newSource.kind === "eventAttribute" && newSource.attributeName === "updatedAt"
                    ? "iso_date"
                    : "none",
              }
            : r
        )
      );
    },
    [rows, onRowsChange, currentQuestion]
  );

  const liveJson = useMemo(
    () => templateToJson([currentQuestion], { [currentQuestion.questionId]: rows }),
    [rows, currentQuestion]
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Mapeo para: <strong>{currentQuestion.questionText}</strong>
        </Typography>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleAddRow}>
          Agregar campo
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "grey.50" }}>
              <TableCell width="40%">Origen</TableCell>
              <TableCell width="12%">Formato</TableCell>
              <TableCell width="12%">Codificación</TableCell>
              <TableCell width="28%">Campo Destino</TableCell>
              <TableCell width="8%" align="center"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No hay mapeos. Haz clic en &quot;Agregar campo&quot;.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Select
                      size="small"
                      value={sourceKey(row.source)}
                      onChange={(e) => {
                        const val = e.target.value as string;
                        if (val === "entityId") {
                          handleSourceChange(row.id, { kind: "entityId" });
                        } else if (val === "answerValue") {
                          handleSourceChange(row.id, {
                            kind: "question",
                            questionId: currentQuestion.questionId,
                            programVersionItemId: currentQuestion.programVersionItemId,
                          });
                        } else if (val.startsWith("eventAttribute:")) {
                          handleSourceChange(row.id, {
                            kind: "eventAttribute",
                            attributeName: val.slice("eventAttribute:".length),
                          });
                        } else if (val.startsWith("resolvedValue:")) {
                          const valueKey = val.slice("resolvedValue:".length);
                          const prop = FARM_PROPERTIES.find((p) => p.valueKey === valueKey);
                          handleSourceChange(row.id, {
                            kind: "resolvedValue",
                            valueKey,
                            label: prop?.label ?? valueKey,
                          });
                        } else if (val === "__new_constant") {
                          handleSourceChange(row.id, {
                            kind: "constant",
                            value: "",
                          });
                        }
                      }}
                      sx={{ fontSize: "0.8rem" }}
                      fullWidth
                    >
                      <ListSubheader>Respuesta</ListSubheader>
                      <MenuItem value="answerValue">
                        Valor de la respuesta
                        {currentQuestion.isFile && (
                          <Chip
                            label="archivo"
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ ml: 1, fontSize: "0.6rem", height: 16 }}
                          />
                        )}
                      </MenuItem>

                      <ListSubheader>Datos del evento</ListSubheader>
                      <MenuItem value="entityId">ID de entidad</MenuItem>
                      {EVENT_ATTRIBUTES.map((attr) => (
                        <MenuItem key={attr.value} value={`eventAttribute:${attr.value}`}>
                          {attr.label}
                        </MenuItem>
                      ))}

                      <ListSubheader>Propiedades de la finca</ListSubheader>
                      {FARM_PROPERTIES.map((prop) => (
                        <MenuItem key={prop.valueKey} value={`resolvedValue:${prop.valueKey}`}>
                          {prop.label}
                        </MenuItem>
                      ))}

                      <ListSubheader>Otros</ListSubheader>
                      <MenuItem value="__new_constant">+ Valor constante</MenuItem>
                    </Select>

                    {row.source.kind === "constant" && (
                      <TextField
                        fullWidth
                        size="small"
                        value={row.source.value}
                        onChange={(e) => {
                          const newSource: SyncSource = {
                            kind: "constant",
                            value: e.target.value,
                          };
                          const label = sourceLabel(newSource, currentQuestion);
                          const suggested = suggestTargetField(newSource, currentQuestion);
                          updateRow(row.id, {
                            source: newSource,
                            displayLabel: label,
                            targetField: row.targetField || suggested,
                          });
                        }}
                        placeholder="Escribe el valor fijo..."
                        sx={{ "& .MuiInputBase-input": { fontSize: "0.8rem" }, mt: 0.5 }}
                      />
                    )}

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
                      <Typography variant="body2" fontWeight={500} noWrap>
                        {row.displayLabel}
                      </Typography>
                      <Chip
                        label={row.source.kind}
                        size="small"
                        variant="outlined"
                        color="default"
                        sx={{ fontSize: "0.65rem", height: 18 }}
                      />
                    </Box>
                  </Box>
                </TableCell>

                <TableCell>
                  <Select
                    size="small"
                    value={row.format}
                    onChange={(e) => updateRow(row.id, { format: e.target.value as MappingFormat })}
                    sx={{ fontSize: "0.8rem" }}
                    fullWidth
                  >
                    <MenuItem value="none">Auto</MenuItem>
                    <MenuItem value="iso_date">Fecha ISO</MenuItem>
                    <MenuItem value="as_number">Numero</MenuItem>
                    <MenuItem value="as_string">Texto</MenuItem>
                  </Select>
                </TableCell>

                <TableCell>
                  <Select
                    size="small"
                    value={row.encode}
                    onChange={(e) => updateRow(row.id, { encode: e.target.value as MappingEncode })}
                    sx={{ fontSize: "0.8rem" }}
                    fullWidth
                  >
                    <MenuItem value="none">Ninguno</MenuItem>
                    <MenuItem value="base64">Base64</MenuItem>
                  </Select>
                </TableCell>

                <TableCell>
                  <TextField
                    fullWidth
                    size="small"
                    value={row.targetField}
                    onChange={(e) => updateRow(row.id, { targetField: e.target.value })}
                    placeholder="ej. document_uuid"
                    error={!row.targetField.trim()}
                    sx={{ "& .MuiInputBase-input": { fontSize: "0.8rem" } }}
                  />
                </TableCell>

                <TableCell align="center">
                  <IconButton size="small" color="error" onClick={() => removeRow(row.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2 }}>
        <JsonPreview
          json={liveJson}
          label={`Mapeo para "${currentQuestion.questionText}"`}
          maxHeight={200}
        />
      </Box>
    </Box>
  );
};

export default FieldMappingBuilder;
